
import React, { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Sparkles, X, HelpCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAndroidTWA } from "@/utils/runtimeContext";
import { useAnalytics } from "@/hooks/useAnalytics";

const PixFloatingButton = () => {
  const { user } = useAuth();
  const { track } = useAnalytics();
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("supportTooltipShown") === "1") return;
    const t1 = setTimeout(() => setShowTooltip(true), 4000);
    const t2 = setTimeout(() => {
      setShowTooltip(false);
      try { sessionStorage.setItem("supportTooltipShown", "1"); } catch {}
    }, 10000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (isAndroidTWA()) return null;

  const isActiveSubscriber = !!user?.isSubscribed && user?.plan && user.plan !== 'free';

  const handleWhatsAppClick = () => {
    track("whatsapp_click", { origin: "floating_modal", intent: "assinar" });
    const message = encodeURIComponent(
      "Olá! Vim através do site IA Palpite Guru e gostaria de me tornar assinante via PIX para ter acesso completo a todos os palpites premium. Podem me ajudar?"
    );
    window.open(`https://wa.me/5573981988263?text=${message}`, "_blank");
    setShowModal(false);
  };

  const handleDoubtClick = () => {
    track("whatsapp_click", { origin: "floating_modal", intent: "duvida" });
    const message = encodeURIComponent(
      "Olá! Estou no site IA Palpite Guru e gostaria de tirar uma dúvida antes de decidir."
    );
    window.open(`https://wa.me/5573981988263?text=${message}`, "_blank");
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <>
      {/* Tooltip flutuante */}
      {showTooltip && !showModal && (
        <div className="fixed right-4 bottom-[calc(9rem+env(safe-area-inset-bottom))] md:bottom-20 z-40 bg-zinc-900/95 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-xl border border-green-400/40 animate-fade-in max-w-[220px]">
          👋 Dúvidas? Fale com a gente no WhatsApp
          <span className="absolute -bottom-1 right-6 w-2 h-2 rotate-45 bg-zinc-900 border-r border-b border-green-400/40" />
        </div>
      )}

      {/* Botão Flutuante */}
      <Button
        onClick={() => { setShowModal(true); setShowTooltip(false); }}
        aria-label="Falar no WhatsApp"
        style={{ backgroundColor: "#25D366" }}
        className="fixed right-4 z-40 hover:brightness-110 text-white font-bold py-3 px-4 rounded-full shadow-xl transition-all duration-300 flex items-center gap-2 text-sm sm:text-base bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-4 ring-2 ring-white/40"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-200 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
        </span>
        <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
        <span>Fale conosco</span>
      </Button>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent 
          className="max-w-sm sm:max-w-md mx-auto p-0 overflow-hidden w-[85%] sm:w-[90%] max-h-[75%] sm:max-h-[80%] overflow-y-auto"
          onInteractOutside={() => setShowModal(false)}
        >
          {/* Header obrigatório para acessibilidade do Dialog */}
          <DialogHeader className="sr-only">
            <DialogTitle>Acesso VIP via PIX</DialogTitle>
            <DialogDescription>Assinatura imediata via WhatsApp</DialogDescription>
          </DialogHeader>
          <div className="animate-fade-in">
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-0">
                {/* Header com gradiente */}
                <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 p-4 pr-14 text-white relative overflow-hidden">
                  <button
                    onClick={handleClose}
                    aria-label="Fechar"
                    className="absolute top-3 right-3 h-10 w-10 flex items-center justify-center rounded-full bg-white text-zinc-900 shadow-lg ring-2 ring-white/70 hover:bg-zinc-100 hover:scale-105 active:scale-95 transition z-20"
                  >
                    <X className="h-5 w-5" strokeWidth={2.5} />
                  </button>

                  {/* Elementos decorativos */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 bg-white/10 rounded-full translate-y-6 -translate-x-6"></div>

                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-5 w-5 text-yellow-300 flex-shrink-0" />
                      <h2 className="text-base sm:text-lg font-bold leading-tight">
                        Como podemos te ajudar?
                      </h2>
                    </div>
                    <p className="text-sm opacity-90">
                      Tire dúvidas ou assine via PIX pelo WhatsApp
                    </p>
                  </div>
                </div>

                {/* Conteúdo principal */}
                <div className="p-4">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                      <strong>Agora você pode se tornar assinante via PIX</strong>, sem precisar de cartão de crédito!
                    </p>
                    
                    <p className="text-xs text-gray-600 mb-4">
                      Tenha acesso imediato a todos os palpites premium: <strong>Milhar, Centena, Duques, Ternos, Grupos, Lotofácil, Mega-Sena, Quina</strong> e muito mais!
                    </p>
                  </div>

                  {/* Benefícios em destaque */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="font-semibold text-green-800 text-sm">✅ Liberação imediata após o pagamento!</span>
                    </div>
                    
                    <div className="space-y-1 text-xs text-green-700">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>Acesso completo a todos os palpites premium</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>Sem necessidade de cartão de crédito</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>Ativação instantânea via WhatsApp</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mb-3">
                    <p className="text-xs text-gray-600 mb-3">
                      Clique no botão abaixo para falar conosco no WhatsApp e garantir seu acesso.
                    </p>
                  </div>

                  {/* Botão WhatsApp em destaque */}
                  {/* Botão dúvida (primário para visitantes) */}
                  <Button
                    onClick={handleDoubtClick}
                    variant="outline"
                    className="w-full mb-2 border-green-500 text-green-700 hover:bg-green-50 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Só quero tirar uma dúvida
                  </Button>

                  {/* Botão WhatsApp em destaque */}
                  {!isActiveSubscriber && (
                    <Button
                      onClick={handleWhatsAppClick}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 text-sm shadow-lg hover:shadow-green-500/25 transition-all duration-300"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Quero assinar via PIX
                    </Button>
                  )}

                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Respondemos rapidamente, sem compromisso.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PixFloatingButton;
